import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Menu, X, ArrowRight, Sparkles, Globe, Phone, Mail, MessageSquare } from 'lucide-react';
import { Logo } from '../ui/Logo';
import { servicesData } from '../../data/servicesData';
import { companyInfo } from '../../data/companyInfo';

interface HeaderProps {
  currentPath: string;
  onNavigate: (path: string) => void;
  onOpenConsultation: () => void;
  onStartProject?: () => void;
}

export const Header: React.FC<HeaderProps> = ({ 
  currentPath, 
  onNavigate, 
  onOpenConsultation,
  onStartProject 
}) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesDropdownOpen, setServicesDropdownOpen] = useState(false);
  const [mobileServicesExpanded, setMobileServicesExpanded] = useState(false);
  const servicesDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 15);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Lock body scroll on mobile when menu drawer is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
    } else {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    };
  }, [mobileMenuOpen]);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (servicesDropdownRef.current && !servicesDropdownRef.current.contains(event.target as Node)) {
        setServicesDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Clean, essential 6 menu items for clear navigation & international SEO
  const navLinks = [
    { label: 'Home', path: '/' },
    { label: 'Services', path: '/services', hasDropdown: true },
    { label: 'Portfolio', path: '/portfolio' },
    { label: 'About Us', path: '/about' },
    { label: 'Careers', path: '/careers', badge: 'Hiring' },
    { label: 'Contact', path: '/contact' },
  ];

  const handleLinkClick = (path: string) => {
    onNavigate(path);
    setMobileMenuOpen(false);
    setServicesDropdownOpen(false);
    setMobileServicesExpanded(false);
  };

  const handleCtaClick = () => {
    setMobileMenuOpen(false);
    if (onStartProject) {
      onStartProject();
    } else {
      onOpenConsultation();
    }
  };

  const handleConsultationClick = () => {
    setMobileMenuOpen(false);
    onOpenConsultation();
  };

  return (
    <>
      <header 
        className={`sticky top-0 z-40 w-full transition-all duration-300 ${
          isScrolled 
            ? 'bg-[#050A13]/95 backdrop-blur-md shadow-xl border-b border-[#1b2f4a]' 
            : 'bg-[#071426] border-b border-[#1b2f4a]/60'
        }`}
      >
        <div className="max-w-7xl mx-auto min-h-[66px] sm:min-h-[82px] px-3 sm:px-6 lg:px-8 py-2 flex items-center justify-between">
          
          {/* Left: Brand Logo */}
          <div className="flex-shrink-0 flex items-center">
            <button 
              onClick={() => handleLinkClick('/')}
              className="focus:outline-none focus:ring-2 focus:ring-[#FF851B] rounded-xl transition-transform hover:scale-[1.02]"
              aria-label="EBON Media Solutions Home"
            >
              <Logo variant="header" />
            </button>
          </div>

          {/* Center: Clean 6-Item Navigation for Desktop */}
          <nav className="hidden md:flex items-center space-x-1 lg:space-x-2" aria-label="Main Navigation">
            {navLinks.map((item) => {
              const isActive = currentPath === item.path || (item.path !== '/' && currentPath.startsWith(item.path));

              // Services Dropdown
              if (item.hasDropdown) {
                return (
                  <div 
                    key={item.label} 
                    className="relative" 
                    ref={servicesDropdownRef}
                    onMouseEnter={() => setServicesDropdownOpen(true)}
                    onMouseLeave={() => setServicesDropdownOpen(false)}
                  >
                    <button
                      onClick={() => handleLinkClick(item.path)}
                      className={`px-3.5 py-2 text-sm font-semibold transition-colors rounded-lg flex items-center gap-1 group ${
                        isActive 
                          ? 'text-[#FF851B]' 
                          : 'text-[#D5DFEC] hover:text-white'
                      }`}
                      aria-expanded={servicesDropdownOpen}
                      aria-haspopup="true"
                    >
                      <span>{item.label}</span>
                      <ChevronDown 
                        size={15} 
                        className={`transition-transform duration-200 text-[#8FA0B8] group-hover:text-white ${
                          servicesDropdownOpen ? 'rotate-180 text-[#FF851B]' : ''
                        }`} 
                      />
                    </button>

                    {/* Services Mega Dropdown */}
                    {servicesDropdownOpen && (
                      <div className="absolute top-full left-0 w-80 bg-[#071426] border border-[#1b2f4a] rounded-2xl shadow-2xl p-3 space-y-1 animate-fade-in z-50">
                        {servicesData.map((s) => (
                          <button
                            key={s.id}
                            onClick={() => handleLinkClick(`/services/${s.slug}`)}
                            className="w-full text-left p-2.5 rounded-xl hover:bg-[#0c1a2d] hover:border-[#159DE8]/30 border border-transparent transition-all group flex items-start justify-between"
                          >
                            <div className="space-y-0.5">
                              <div className="text-xs font-bold text-white group-hover:text-[#FF851B] transition-colors">
                                {s.title}
                              </div>
                              <div className="text-[11px] text-[#8FA0B8] line-clamp-1">
                                {s.shortDescription}
                              </div>
                            </div>
                            <ArrowRight size={13} className="text-[#8FA0B8] group-hover:text-[#FF851B] group-hover:translate-x-0.5 transition-all mt-1 shrink-0 ml-2" />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }

              // Standard Link
              return (
                <button
                  key={item.label}
                  onClick={() => handleLinkClick(item.path)}
                  className={`px-3.5 py-2 text-sm font-semibold transition-colors rounded-lg relative flex items-center gap-1.5 ${
                    isActive 
                      ? 'text-[#FF851B]' 
                      : 'text-[#D5DFEC] hover:text-white'
                  }`}
                >
                  <span>{item.label}</span>
                  {item.badge && (
                    <span className="px-1.5 py-0.5 rounded-full text-[9px] font-extrabold bg-[#00C853]/15 text-[#00C853] border border-[#00C853]/40 tracking-wide uppercase">
                      {item.badge}
                    </span>
                  )}
                  {isActive && (
                    <span className="absolute bottom-0 left-3 right-3 h-[2px] bg-[#FF851B] rounded-full" />
                  )}
                </button>
              );
            })}
          </nav>

          {/* Right: Desktop Primary CTA */}
          <div className="hidden md:flex items-center gap-3">
            <button
              onClick={handleCtaClick}
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-gradient-to-r from-[#FF5A36] to-[#FF851B] text-white text-xs sm:text-sm font-bold shadow-md hover:shadow-glow-orange hover:scale-[1.02] active:scale-[0.98] transition-all duration-200"
            >
              <Sparkles size={14} className="fill-white/20" />
              <span>Start Your Project</span>
            </button>
          </div>

          {/* Mobile Right Controls: Compact CTA + Hamburger Button */}
          <div className="flex md:hidden items-center gap-2">
            <button
              onClick={handleCtaClick}
              className="px-3 py-1.5 rounded-full bg-gradient-to-r from-[#FF5A36] to-[#FF851B] text-white text-[11px] font-bold shadow-sm flex items-center gap-1 active:scale-95 transition-transform"
              aria-label="Start Project"
            >
              <Sparkles size={12} />
              <span>Start</span>
            </button>
            
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="w-10 h-10 rounded-xl bg-[#0c1a2d] border border-[#1b2f4a] text-white flex items-center justify-center hover:bg-white/10 active:scale-95 transition-transform focus:outline-none focus:ring-2 focus:ring-[#FF851B]"
              aria-label="Toggle navigation menu"
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </header>

      {/* Full-Screen Mobile Drawer for iOS & Android with Safe Areas */}
      {mobileMenuOpen && (
        <div 
          className="md:hidden fixed inset-0 z-50 bg-[#050A13] flex flex-col justify-between overflow-y-auto pt-safe pb-safe animate-fade-in touch-scroll"
          role="dialog"
          aria-modal="true"
        >
          {/* Top Bar inside Drawer */}
          <div className="px-4 py-3.5 border-b border-[#1b2f4a] flex items-center justify-between bg-[#071426] sticky top-0 z-10">
            <button 
              onClick={() => handleLinkClick('/')}
              className="text-left focus:outline-none"
            >
              <Logo variant="header" className="scale-90 origin-left" />
            </button>
            <button
              onClick={() => setMobileMenuOpen(false)}
              className="w-10 h-10 rounded-xl bg-[#0c1a2d] border border-[#1b2f4a] text-white flex items-center justify-center hover:bg-white/10 active:scale-95 transition-transform"
              aria-label="Close navigation menu"
            >
              <X size={22} className="text-[#FF851B]" />
            </button>
          </div>

          {/* Navigation Links Area */}
          <div className="flex-1 px-4 py-5 space-y-2 overflow-y-auto">
            {navLinks.map((item) => {
              const isActive = currentPath === item.path || (item.path !== '/' && currentPath.startsWith(item.path));
              
              if (item.hasDropdown) {
                return (
                  <div key={item.label} className="border-b border-[#1b2f4a]/40 pb-2">
                    <button
                      onClick={() => setMobileServicesExpanded(!mobileServicesExpanded)}
                      className={`w-full py-3 px-3.5 rounded-xl font-bold text-base flex items-center justify-between transition-colors ${
                        isActive ? 'text-[#FF851B] bg-[#0c1a2d]' : 'text-white hover:bg-white/5'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span>{item.label}</span>
                        <span className="text-[10px] text-[#159DE8] bg-[#159DE8]/10 px-2 py-0.5 rounded border border-[#159DE8]/30 font-normal">
                          6 Capabilities
                        </span>
                      </div>
                      <ChevronDown 
                        size={18} 
                        className={`text-[#8FA0B8] transition-transform duration-200 ${
                          mobileServicesExpanded ? 'rotate-180 text-[#FF851B]' : ''
                        }`} 
                      />
                    </button>

                    {/* Expandable Services List */}
                    {mobileServicesExpanded && (
                      <div className="mt-1 space-y-1 pl-3 pr-1 py-2 bg-[#071426] rounded-xl border border-[#1b2f4a]/60 animate-fade-in">
                        <button
                          onClick={() => handleLinkClick('/services')}
                          className="w-full text-left py-2 px-3 text-xs font-bold text-[#FF851B] flex items-center justify-between border-b border-[#1b2f4a]/50"
                        >
                          <span>Explore All Services Overview</span>
                          <ArrowRight size={13} />
                        </button>
                        {servicesData.map((s) => (
                          <button
                            key={s.id}
                            onClick={() => handleLinkClick(`/services/${s.slug}`)}
                            className="w-full text-left py-2 px-3 text-xs text-[#D5DFEC] hover:text-[#159DE8] active:bg-[#0c1a2d] rounded-lg flex items-center justify-between"
                          >
                            <span>{s.title}</span>
                            <span className="text-[#8FA0B8] text-[11px]">→</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <div key={item.label} className="border-b border-[#1b2f4a]/40 pb-2">
                  <button
                    onClick={() => handleLinkClick(item.path)}
                    className={`w-full py-3 px-3.5 rounded-xl font-bold text-base flex items-center justify-between transition-colors ${
                      isActive ? 'text-[#FF851B] bg-[#0c1a2d]' : 'text-white hover:bg-white/5'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span>{item.label}</span>
                      {item.badge && (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-extrabold bg-[#00C853]/20 text-[#00C853] border border-[#00C853]/40">
                          {item.badge}
                        </span>
                      )}
                    </div>
                    <ArrowRight size={16} className={isActive ? 'text-[#FF851B]' : 'text-[#8FA0B8]'} />
                  </button>
                </div>
              );
            })}

            {/* Quick Consultation Trigger inside Mobile Menu */}
            <div className="pt-4 space-y-2.5">
              <button
                onClick={handleCtaClick}
                className="w-full py-3.5 px-4 rounded-xl bg-gradient-to-r from-[#FF5A36] to-[#FF851B] text-white font-bold text-sm shadow-glow-orange flex items-center justify-center gap-2 active:scale-98 transition-transform"
              >
                <Sparkles size={16} />
                <span>Start Your Project Brief</span>
              </button>

              <button
                onClick={handleConsultationClick}
                className="w-full py-3.5 px-4 rounded-xl bg-[#0c1a2d] border border-[#1b2f4a] text-white font-semibold text-xs hover:border-[#159DE8] flex items-center justify-center gap-2 active:scale-98 transition-transform"
              >
                <MessageSquare size={15} className="text-[#159DE8]" />
                <span>Book a Strategy Consultation</span>
              </button>
            </div>
          </div>

          {/* Bottom Drawer Footer: Direct Contact for Thumb Reach */}
          <div className="p-4 bg-[#071426] border-t border-[#1b2f4a] space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <a
                href={`tel:${companyInfo.contact.phone}`}
                className="p-2.5 rounded-xl bg-[#050A13] border border-[#1b2f4a] flex items-center justify-center gap-2 text-xs font-semibold text-white active:scale-95"
              >
                <Phone size={14} className="text-[#FF851B]" />
                <span>Direct Call</span>
              </a>

              <a
                href={`mailto:${companyInfo.contact.email}`}
                className="p-2.5 rounded-xl bg-[#050A13] border border-[#1b2f4a] flex items-center justify-center gap-2 text-xs font-semibold text-white active:scale-95"
              >
                <Mail size={14} className="text-[#159DE8]" />
                <span>Email Us</span>
              </a>
            </div>

            <div className="text-center text-[10px] text-[#8FA0B8] flex items-center justify-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-[#00C853]" />
              <span>International Delivery: USA • UK • Canada • India</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
